/**
 * ZYYL storefront helpers for the Horizon theme.
 *
 * - <zyyl-variant-sync>: re-renders itself from the section HTML Horizon fetches on variant change.
 * - <zyyl-delivery-date>: "delivered between X and Y" in working days (Sundays skipped).
 * - <zyyl-bundle-ladder>: highlights the Buy 2 / Buy 3 step the cart has reached.
 * - addToCart(): adds a variant the same way Horizon's product form does, so the cart
 *   drawer, cart bubble and Razorpay listeners all see the standard cart event.
 */
import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';

/**
 * Replaces this element with its freshly rendered copy after a variant change.
 * Markup: <zyyl-variant-sync data-sync-id="{{ block.id }}">…</zyyl-variant-sync>
 */
class ZyylVariantSync extends HTMLElement {
  #section = null;

  connectedCallback() {
    this.#section = this.closest('.shopify-section, dialog');
    this.#section?.addEventListener(StandardEvents.productSelect, this.#onProductSelect);
  }

  disconnectedCallback() {
    this.#section?.removeEventListener(StandardEvents.productSelect, this.#onProductSelect);
  }

  #onProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      ?.then(({ detail }) => {
        const fresh = detail?.html?.querySelector(`zyyl-variant-sync[data-sync-id="${this.dataset.syncId}"]`);
        if (fresh) this.innerHTML = fresh.innerHTML;
      })
      .catch(() => {});
  };
}

/**
 * Renders a delivery window counted in working days from today.
 * Attributes: data-min, data-max (working days), data-template ("{from}" / "{to}" placeholders).
 */
class ZyylDeliveryDate extends HTMLElement {
  connectedCallback() {
    const min = parseInt(this.dataset.min, 10) || 4;
    const max = Math.max(parseInt(this.dataset.max, 10) || 7, min);
    const format = new Intl.DateTimeFormat('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
    const from = format.format(addWorkingDays(new Date(), min));
    const to = format.format(addWorkingDays(new Date(), max));
    const target = this.querySelector('[data-zyyl-delivery-text]');
    if (!target) return;

    const template = this.dataset.template || 'Delivered between {from} and {to}';
    target.innerHTML = escapeHtml(template)
      .replace('{from}', `<strong>${from}</strong>`)
      .replace('{to}', `<strong>${to}</strong>`);
  }
}

/**
 * @param {Date} date
 * @param {number} days
 */
function addWorkingDays(date, days) {
  const result = new Date(date);
  let added = 0;
  while (added < days) {
    result.setDate(result.getDate() + 1);
    if (result.getDay() !== 0) added += 1;
  }
  return result;
}

/** @param {string} value */
function escapeHtml(value) {
  const div = document.createElement('div');
  div.textContent = value;
  return div.innerHTML;
}

/**
 * Highlights the step of the bundle offer the cart has reached and updates the status line.
 * Steps: [data-step="1|2|3"]. Messages come from data-msg-0 … data-msg-3.
 */
class ZyylBundleLadder extends HTMLElement {
  connectedCallback() {
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    this.#refresh();
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
  }

  #onCartUpdate = (event) => {
    event.promise?.then(() => this.#refresh()).catch(() => {});
  };

  async #refresh() {
    try {
      const response = await fetch(`${window.Theme?.routes?.cart_url || '/cart'}.js`, {
        headers: { Accept: 'application/json' },
      });
      const cart = await response.json();
      this.#render(cart.item_count || 0);
    } catch {
      this.#render(0);
    }
  }

  /** @param {number} count */
  #render(count) {
    const reached = Math.min(count, 3);
    this.querySelectorAll('[data-step]').forEach((step) => {
      const value = parseInt(step.getAttribute('data-step'), 10);
      step.classList.toggle('is-reached', count >= value);
      step.classList.toggle('is-current', reached === value);
    });

    const status = this.querySelector('[data-zyyl-ladder-status]');
    const message = this.getAttribute(`data-msg-${reached}`);
    if (status && message) status.textContent = message;
  }
}

/**
 * Adds a variant to the cart and emits the standard cart event Horizon listens to.
 * @param {string|number} variantId
 * @param {number} [quantity]
 * @returns {Promise<object>} the updated cart
 */
export async function addToCart(variantId, quantity = 1) {
  const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
    .map((element) => element instanceof HTMLElement && element.dataset.sectionId)
    .filter(Boolean);

  const body = new FormData();
  body.append('id', String(variantId));
  body.append('quantity', String(quantity));
  if (sectionIds.length) body.append('sections', sectionIds.join(','));

  const deferred = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: String(variantId), quantity }],
      promise: deferred.promise,
    })
  );

  try {
    const response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' },
      body,
    });
    const result = await response.json();
    const cart = await (await fetch(`${window.Theme?.routes?.cart_url || '/cart'}.js`)).json();

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'zyyl-lookbook',
        itemCount: quantity,
        sections: result.sections,
        didError: Boolean(result.status),
      },
    });

    if (result.status) {
      document.dispatchEvent(
        new CartErrorEvent({ error: result.description || result.message || 'Add to cart failed', code: 'INVALID' })
      );
      throw new Error(result.description || result.message || 'Add to cart failed');
    }

    return cart;
  } catch (error) {
    deferred.reject(error);
    throw error;
  }
}

/**
 * Mini product quick add: a "+" button that reveals size chips (or adds directly when the
 * product has one variant). Markup:
 * <zyyl-quick-add>
 *   <button data-qa-toggle aria-expanded="false" aria-controls="ID">+</button>   (multi-variant)
 *   <div id="ID" hidden><button data-variant-id="…">S</button>…</div>
 *   or <button data-variant-id="…">+</button>                                   (single variant)
 *   <span data-qa-status aria-live="polite"></span>
 * </zyyl-quick-add>
 */
class ZyylQuickAdd extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  #onClick = async (event) => {
    const button = event.target instanceof Element ? event.target.closest('button') : null;
    if (!button || !this.contains(button)) return;

    if (button.hasAttribute('data-qa-toggle')) {
      const panel = document.getElementById(button.getAttribute('aria-controls'));
      const open = button.getAttribute('aria-expanded') === 'true';
      button.setAttribute('aria-expanded', String(!open));
      panel?.toggleAttribute('hidden', open);
      if (!open) panel?.querySelector('button:not([disabled])')?.focus();
      return;
    }

    if (!button.dataset.variantId || button.getAttribute('aria-busy') === 'true') return;
    const status = this.querySelector('[data-qa-status]');
    button.setAttribute('aria-busy', 'true');
    try {
      await addToCart(button.dataset.variantId, 1);
      this.classList.add('is-added');
      const toggle = this.querySelector('[data-qa-toggle]');
      if (toggle) {
        toggle.setAttribute('aria-expanded', 'false');
        document.getElementById(toggle.getAttribute('aria-controls'))?.setAttribute('hidden', '');
      }
      if (status) status.textContent = 'Added to bag';
    } catch (error) {
      if (status) status.textContent = error?.message || 'Could not add. Please try again.';
    } finally {
      button.removeAttribute('aria-busy');
    }
  };
}

/**
 * Sticky add-to-cart size guard. The first size is pre-selected, so until the shopper picks one
 * (<sticky-add-to-cart data-zyyl-needs-choice="true">), the bar's button reads "Select size" and
 * scrolls to the size picker instead of adding. Picking any option adds `zyyl-size-chosen` to <html>,
 * which survives Horizon re-rendering the section on variant change.
 * Listens on window in the capture phase so it runs before Horizon's document-level on:click.
 */
const chosenClass = 'zyyl-size-chosen';
const mainPicker = () =>
  document.querySelector('.product-information variant-picker, product-information variant-picker') ??
  document.querySelector('variant-picker:not(product-card variant-picker)');

window.addEventListener(
  'change',
  (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target?.closest('variant-picker') || target.closest('product-card, dialog')) return;
    document.documentElement.classList.add(chosenClass);
  },
  { capture: true }
);

window.addEventListener(
  'click',
  (event) => {
    const button = event.target instanceof Element ? event.target.closest('.sticky-add-to-cart__button') : null;
    if (!button) return;
    const sticky = button.closest('sticky-add-to-cart');
    if (sticky?.dataset.zyylNeedsChoice !== 'true' || document.documentElement.classList.contains(chosenClass)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const picker = mainPicker();
    if (!picker) return;
    picker.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
    picker.classList.remove('zyyl-attention');
    void picker.offsetWidth; // restart the highlight animation
    picker.classList.add('zyyl-attention');
    picker.querySelector('input:not([disabled]), button:not([disabled])')?.focus({ preventScroll: true });
  },
  { capture: true }
);

/**
 * <zyyl-sort>: native sort picker for the phone filter bar. Reloads with ?sort_by=, keeping filters.
 *   <zyyl-sort><span>Sort <small data-sort-current>Featured</small></span><select>…</select></zyyl-sort>
 */
class ZyylSort extends HTMLElement {
  connectedCallback() {
    this.querySelector('select')?.addEventListener('change', this.#onChange);
  }

  disconnectedCallback() {
    this.querySelector('select')?.removeEventListener('change', this.#onChange);
  }

  #onChange = (event) => {
    const select = event.currentTarget;
    const current = this.querySelector('[data-sort-current]');
    if (current) current.textContent = select.selectedOptions[0]?.textContent ?? '';
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', select.value);
    url.searchParams.delete('page');
    window.location.assign(url.toString());
  };
}

if (!customElements.get('zyyl-sort')) customElements.define('zyyl-sort', ZyylSort);
if (!customElements.get('zyyl-quick-add')) customElements.define('zyyl-quick-add', ZyylQuickAdd);
if (!customElements.get('zyyl-variant-sync')) customElements.define('zyyl-variant-sync', ZyylVariantSync);
if (!customElements.get('zyyl-delivery-date')) customElements.define('zyyl-delivery-date', ZyylDeliveryDate);
if (!customElements.get('zyyl-bundle-ladder')) customElements.define('zyyl-bundle-ladder', ZyylBundleLadder);
